import React, { Component, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { findDOMNode } from 'react-dom';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Route, useLocation, withRouter } from 'react-router-dom';
import smoothScroll from 'smoothscroll';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { actions as appActions } from '../App/actions/actions';
import { actions as expiredSessionActions } from 'components/ExpiredSession/actions/actions';
import { actions as practiceProfileActions } from './actions/actions';
import { actions as loginActions } from '../EntryPage/actions/actions';
import { actions as shellActions } from '../components/UIContainer/MobileUI/MainShell/actions/actions';
import { practiceProfilePropType } from './propTypes';
import { profilePropType } from '../components/UIContainer/MobileUI/MainShell/propTypes';
import renderOnResolutionChange from '../components/RenderOnResolutionChange';
import DesktopHeader from './components/DesktopView/DesktopHeader';
import DesktopAppointments from './components/DesktopView/DesktopAppointments';
import DoctorPopUp from './DoctorProfilePage';
import ScriptPopUp from './ScriptTriagePopUp';
import BookingPopUp from './BookingPopUpDesktop';
import LoginDetailsPopup from './LoginDetailsPopup';
import MobileView from './components/MobileView';
import DesktopView from './components/DesktopView';
import Markup from './markup';
import NotOnline from './components/NotOnline';
import styles from './practiceProfilePage.module.css';
import { clearAppointmentData } from '../selectors/appointmentSelectors';
import { go } from '../routes';
import { isDesktop, ENV } from '../config';
import Transition from '../components/Transition';
import cssVars from 'css-vars-ponyfill';
import { actions as profileActions } from 'components/UIContainer/MobileUI/MainShell/actions/profileActions';
import { useComponentDidMount } from 'libs/newHttp';
import Loading from 'components/loadingSpinner';
import { usePracticesStore, useAppointmentStore } from 'store/mobx/mobx';
import { height } from 'styled-system';

const Background = styled.div`
  background-color: #f5f5f5;
  position: absolute;
  height: 100%;
  width: 100%;
  transition: all 0.3s linear;
  z-index: 10;
  visibility: visible;
`;

const PracticeProfilePage = observer(
  ({
    addAppStyles,
    removeAppStyles,
    getPracticeProfile,
    clearAppointmentData,
    setComponentIndex,
    logout,
    isFetching,
    practice,
    token,
    error,
    isLoggedIn,
    forceUpdate,
    user,
    getProfile,
    showExpiredSession,
  }) => {
    const store = usePracticesStore();
    const apptStore = useAppointmentStore();
    const appointmentsRef = useRef(HTMLDivElement);
    const pageRef = useRef(HTMLDivElement);
    const location = useLocation();
    const key = location.pathname.split('/')[2];
    useComponentDidMount(() => {
      //showExpiredSession();
      cssVars({});
      clearAppointmentData();
      store.getPracticeDetails(key, () => {});
      getPracticeProfile(key);
      addAppStyles([styles.app]);
      if (token) {
        getProfile(token);
      }
    });

    const handleLogOut = () => {
      logout(token);
    };
    const smoothScroll = () => {
      // eslint-disable-next-line react/no-find-dom-node
      appointmentsRef.current.scrollIntoView({ behavior: 'smooth' });
    };
    const locationSelected = location => {
      go.practiceProfile(location.UrlName);
      store.getPracticeDetails(location.UrlName, () => {});
      getPracticeProfile(location.UrlName);
    };
    const goVensaDotCom = () => {
      window.location = 'https://vensa.com';
    };
    const goScripts = () => {
      if (token) {
        if (!isDesktop()) setComponentIndex(2);
        go.prescriptions();
      } else {
        go.scriptTriage(key);
      }
    };

    const bookNow = id => {
      if (practice.MustLoginBeforeBooking) {
        if (token) {
          go.patientSelection(practice.UrlName);
        } else {
          go.loginTriage(practice.UrlName);
        }
      } else {
        go.practiceBooking(practice.UrlName);
      }
    };

    return (
      <div className={styles.page} style={{ height: '100%' }}>
        <Background style={{ visibility: isFetching ? 'visible' : 'hidden' }} />
        <Loading
          isFetching={isFetching}
          style={{ position: 'absolute', top: '6rem' }}
        />
        {isDesktop() && (
          <DesktopHeader
            practice={practice}
            isLoggedIn={isLoggedIn}
            user={user}
            logOut={handleLogOut}
            goHome={() => go.home()}
            goVensa={goVensaDotCom}
            id={practice.Id}
          />
        )}
        {!isFetching && (
          <div className={styles.container}>
            {practice.Name && ENV() === 'production' && (
              <Markup practice={practice} />
            )}
            {error && error.status === 404 ? (
              <div className={styles.error}>
                Sorry, the practice you are looking for was not found
              </div>
            ) : (
              <div className={styles.contentContainer} ref={pageRef}>
                {practice.Name && !isFetching && (
                  <div>
                    {isDesktop() ? (
                      <div className={styles.mainContent}>
                        <DesktopView
                          practice={practice}
                          goScripts={goScripts}
                          scrollFn={smoothScroll}
                          locationSelected={locationSelected}
                        />
                        {practice.IsOnline ? (
                          <DesktopAppointments
                            innerRef={appointmentsRef}
                            practice={practice}
                            id={key}
                            apptStore={apptStore}
                          />
                        ) : (
                          <NotOnline clinic={practice} />
                        )}

                        <Transition
                          key={key}
                          transitionName={{
                            enter: styles.enter,
                            enterActive: styles.enterActive,
                            appear: styles.enter,
                            appearActive: styles.enterActive,
                            leave: styles.leave,
                            leaveActive: styles.leaveActive,
                          }}
                        >
                          <Route
                            path="/practice-profile/:orgId/doctor/:id"
                            component={DoctorPopUp}
                          />
                          <Route
                            path="/practice-profile/:orgId/request-prescription"
                            component={ScriptPopUp}
                          />
                          <Route
                            path="/practice-profile/:orgId/book/:booking"
                            component={BookingPopUp}
                          />
                          <Route
                            path="/practice-profile/:orgId/:type(login|selection|details|guardian|welcome)"
                            component={LoginDetailsPopup}
                          />
                        </Transition>
                      </div>
                    ) : (
                      <MobileView
                        practice={practice}
                        page={pageRef.current}
                        isLoggedIn={isLoggedIn}
                        logout={handleLogOut}
                        isFetching={isFetching}
                        bookNow={bookNow}
                        goHome={goScripts}
                      />
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    );
  },
);

const mapStateToProps = state => ({
  token: state.loginReducer.token,
  isFetching: state.practiceProfileReducer.isFetching,
  practice: state.practiceProfileReducer.profile,
  user: state.profileReducer.profile,
  error: state.practiceProfileReducer.error,
  isLoggedIn: state.loginReducer.token !== null,
});
const mapDispatchToProps = dispatch => ({
  addAppStyles: bindActionCreators(appActions.addAppStyles, dispatch),
  //showExpiredSession: bindActionCreators(expiredSessionActions.showExpiredSession, dispatch),
  removeAppStyles: bindActionCreators(appActions.removeAppStyles, dispatch),
  getProfile: bindActionCreators(profileActions.getProfile, dispatch),
  getPracticeProfile: bindActionCreators(
    practiceProfileActions.getPracticeProfile,
    dispatch,
  ),
  clearAppointmentData: clearAppointmentData(dispatch),
  setComponentIndex: bindActionCreators(
    shellActions.setComponentIndex,
    dispatch,
  ),
  logout: bindActionCreators(loginActions.logout, dispatch),
});

// eslint-disable-next-line max-len
export default withRouter(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  )(renderOnResolutionChange(PracticeProfilePage)),
);
export { styles };
